import type { ReactElement } from 'react';
import { useMemo, useCallback, useRef, useState } from 'react';
import { DataTable } from '@/components/DataTable';
import { getBrowserToolRowLabel } from '@/components/shared/shadow-ai/toolApprovalRowLabels';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import type { DataFetchParams, DataTableProps } from '@/components/DataTable';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import type {
  BrowserAiToolListItem,
  BrowserAiNavigationType,
} from '@kirin/api-client/contracts/shadow-ai-browser-tools';
import {
  useToolUsers,
  SHADOW_AI_REFRESH_INTERVAL,
} from '@/hooks/api/useShadowAiTools';
import { queryKeys } from '@/hooks/api/queryKeys';
import {
  SHADOW_AI_TABLE_SCROLL_CLASS,
  BROWSER_TOOLS_HELPER_LINE,
  BrowserToolEvidence,
  BrowserToolEvidenceFilters,
} from '@/components/shared/shadow-ai';
import { ToolUsersTable } from '../ToolUsersTable';
import {
  BrowserHistoryDetailDialog,
  type BrowserHistoryDetailTool,
} from '@/components/shared/shadow-ai';
import {
  TOOL_APPROVAL_SCOPES,
  TOOL_APPROVAL_TABS,
  type ToolApprovalFilter,
} from '@kirin/api-client/contracts/shadow-ai-tool-approvals';
import { createToolApprovalColumn } from '@/components/shared/shadow-ai/toolApprovalColumn';
import { useToolApprovalTable } from '@/components/shared/shadow-ai/useToolApprovalTable';
import { Skeleton } from '@/components/ui/skeleton';
import { createBrowserAiToolsColumns } from './columns';
import { fetchBrowserAiTools, type BrowserAiToolsEvidenceFilters } from './api';
import { BROWSER_AI_TOOLS_TABLE_CONFIG } from './constants';

function ExpandedToolRow({
  organizationId,
  row,
  onUserClick,
  scopeUserId,
}: {
  organizationId: string;
  row: BrowserAiToolListItem;
  /** Narrows the expanded user list to one member (user-scope page). */
  scopeUserId?: string;
  onUserClick?: (
    userId: string,
    userInfo?: { email?: string; name?: string }
  ) => void;
}): ReactElement {
  const domain = row.domain;
  const {
    data: usersData,
    isError,
    isLoading,
  } = useToolUsers(organizationId, domain, true, scopeUserId, {
    // KIR-5296: only this row's users, not every category of the domain.
    category: row.category,
    source: 'browser',
  });

  return (
    <div className="space-y-3 pl-8">
      <BrowserToolEvidence evidence={row} />
      {isError ? (
        <div className="text-sm text-destructive">
          Failed to load users for this tool. Please try again.
        </div>
      ) : isLoading ? (
        <div className="text-sm text-muted-foreground">Loading users...</div>
      ) : (
        // KIR-3812: browser tools never capture a logged-in account, so the
        // per-user "Logged-in User" column is always blank here — keep it hidden.
        <ToolUsersTable
          users={usersData?.users ?? []}
          onUserClick={onUserClick}
          showLoggedInUser={false}
        />
      )}
    </div>
  );
}

/** Browser rows are one per domain, which is also the approval key. */
const getBrowserToolDomain = (row: BrowserAiToolListItem): string => row.domain;
// Rows are grouped by (domain, category), so one domain can fill two rows; a
// domain-only row id let React keep stale rows after a filter (KIR-5098).
const getBrowserToolRowId = (row: BrowserAiToolListItem): string =>
  `${row.domain}::${row.category}`;

function getQueryKeysFromParams(
  organizationId: string,
  params: DataFetchParams,
  evidenceFilters: BrowserAiToolsEvidenceFilters,
  scopeUserId?: string,
  approval?: ToolApprovalFilter
) {
  const base = queryKeys.shadowAi.browserAiToolsDataTable(organizationId, {
    page: params.pagination.pageIndex + 1,
    limit: params.pagination.pageSize,
    filters: params.filters,
    sortBy: params.sort?.field,
    sortOrder: params.sort?.order,
    search: params.search,
    navigationType: evidenceFilters.navigationType,
    minVisits: evidenceFilters.minVisits,
    // KIR-4854: only keyed when the flag is on (undefined drops out).
    ...(approval ? { approval } : {}),
  });
  return scopeUserId ? [...base, { scopeUserId }] : base;
}

interface DataTableBrowserAiToolsContentProps {
  organizationId: string;
  /** Omit on a surface already scoped to one user — rows render non-interactive. */
  onUserClick?: (
    userId: string,
    domain: string,
    userInfo?: { email?: string; name?: string }
  ) => void;
  /**
   * KIR-3495: restrict every row — and its aggregates — to a single org member
   * (Shadow AI user-scope page). Omit for the org-wide table.
   */
  scopeUserId?: string;
  /**
   * Overrides the table id, which keys URL state and local preferences. The
   * user-scope page passes its own so its state never collides with the
   * org-wide table's.
   */
  tableId?: string;
  /** Seeds the initially expanded rows (deep links from the org table). */
  initialState?: DataTableProps<BrowserAiToolListItem>['initialState'];
  /**
   * KIR-3495: domain a deep link points at. Scoped rows open a detail dialog
   * instead of expanding, so the link is honoured by opening that dialog once
   * the row arrives — seeding `expandedRowIds` would be inert here.
   */
  openDomainOnLoad?: string;
  /** Overrides the card heading (the user-scope page says "Browser History"). */
  title?: string;
}

export function DataTableBrowserAiToolsContent({
  organizationId,
  onUserClick,
  scopeUserId,
  tableId = BROWSER_AI_TOOLS_TABLE_CONFIG.tableId,
  initialState,
  title = 'Browser AI Tools',
  openDomainOnLoad,
}: DataTableBrowserAiToolsContentProps): ReactElement {
  // KIR-5098: Endpoints column header + hostnames follow endpoint_identifier.
  const { flags } = useFeatureFlags([
    flagsmithFeatureFlags.ENDPOINT_IDENTIFIER,
  ]);
  const showEndpointName =
    flags[flagsmithFeatureFlags.ENDPOINT_IDENTIFIER] ?? false;
  const [navigationType, setNavigationType] = useState<
    BrowserAiNavigationType | undefined
  >(undefined);
  const [minVisits, setMinVisits] = useState<number | undefined>(undefined);
  // KIR-3495: the pre-conversion user-scope page opened a Browser History row
  // in a detail dialog rather than expanding it — restore that when scoped.
  const [detailTool, setDetailTool] = useState<BrowserHistoryDetailTool | null>(
    null
  );

  const openDetail = useCallback(
    (row: BrowserAiToolListItem) =>
      setDetailTool({
        toolId: row.domain,
        name: row.name,
        category: row.category,
        addedAt: row.firstDetected,
        lastUsed: row.lastSeen,
        extras: row.extras ?? null,
        // The row's own KIR-3890 fields are the aggregate the Visits column
        // renders; `extras` is one machine's raw agent output and is absent on
        // legacy rows, so the dialog must not derive visit evidence from it.
        evidence: {
          domain: row.domain,
          visitCount: row.visitCount,
          realVisitCount: row.realVisitCount,
          navigationType: row.navigationType,
          sampleMatchedUrl: row.sampleMatchedUrl,
          firstDetected: row.firstDetected,
          lastSeen: row.lastSeen,
          realCount: row.realCount,
          redirectCount: row.redirectCount,
          addressBarCount: row.addressBarCount,
          adsTrackingCount: row.adsTrackingCount,
        },
      }),
    []
  );

  const approvalTable = useToolApprovalTable({
    organizationId,
    tableId,
    tab: TOOL_APPROVAL_TABS.BROWSER,
    scope: TOOL_APPROVAL_SCOPES.BROWSER,
    getRowId: getBrowserToolRowId,
    getToolId: getBrowserToolDomain,
    getRowLabel: getBrowserToolRowLabel,
    selectionResetKey: `${navigationType ?? ''}|${minVisits ?? ''}`,
    scopeUserId,
    countsRefetchInterval: SHADOW_AI_REFRESH_INTERVAL,
  });
  const { approvalFilter, writeGate } = approvalTable;
  const approvalEnabled = approvalTable.enabled;

  const columns = useMemo(() => {
    const base = createBrowserAiToolsColumns(
      organizationId,
      navigationType,
      scopeUserId,
      showEndpointName
    );
    if (!approvalEnabled) return base;
    return [
      ...base,
      createToolApprovalColumn<BrowserAiToolListItem>({
        getToolId: getBrowserToolDomain,
        scope: TOOL_APPROVAL_SCOPES.BROWSER,
        organizationId,
        writeGate,
      }),
    ];
  }, [
    organizationId,
    navigationType,
    scopeUserId,
    showEndpointName,
    approvalEnabled,
    writeGate,
  ]);

  // Include organizationId + evidence filters in countScope so knownTotal cache
  // is invalidated when they change (the component is not remounted), and
  // scopeUserId so it is invalidated when drilling into a different member.
  const countScope = useMemo(
    () => ({
      organizationId,
      navigationType,
      minVisits,
      scopeUserId,
      approval: approvalFilter,
    }),
    [organizationId, navigationType, minVisits, scopeUserId, approvalFilter]
  );

  const queryFn = useCallback(
    (params: DataFetchParams, signal?: AbortSignal) =>
      fetchBrowserAiTools(
        organizationId,
        params,
        signal,
        { navigationType, minVisits },
        scopeUserId,
        approvalFilter
      ),
    [organizationId, navigationType, minVisits, scopeUserId, approvalFilter]
  );

  const queryKeyFn = useCallback(
    (params: DataFetchParams) =>
      getQueryKeysFromParams(
        organizationId,
        params,
        { navigationType, minVisits },
        scopeUserId,
        approvalFilter
      ),
    [organizationId, navigationType, minVisits, scopeUserId, approvalFilter]
  );

  // Open the deep-linked row's dialog once, when its row first arrives.
  const openedDeepLinkRef = useRef(false);
  const handleMeta = useCallback(
    (rows: BrowserAiToolListItem[]) => {
      if (!openDomainOnLoad || openedDeepLinkRef.current) return;
      const match = rows.find(r => r.domain === openDomainOnLoad);
      if (!match) return;
      openedDeepLinkRef.current = true;
      openDetail(match);
    },
    [openDomainOnLoad, openDetail]
  );

  const queryFnWithDeepLink = useCallback(
    async (params: DataFetchParams, signal?: AbortSignal) => {
      const response = await queryFn(params, signal);
      handleMeta(response.data);
      return response;
    },
    [queryFn, handleMeta]
  );

  const queryConfig = useMemo(
    () => ({
      staleTime: 30000,
      refetchOnWindowFocus: false,
      refetchInterval: SHADOW_AI_REFRESH_INTERVAL,
    }),
    []
  );

  return (
    <Card>
      <CardHeader className="flex flex-col gap-2 pb-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <CardTitle className="text-base">{title}</CardTitle>
          <CardDescription>{BROWSER_TOOLS_HELPER_LINE}</CardDescription>
        </div>
        <div className="shrink-0">
          <BrowserToolEvidenceFilters
            navigationType={navigationType}
            onNavigationTypeChange={setNavigationType}
            minVisits={minVisits}
            onMinVisitsChange={setMinVisits}
          />
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {approvalTable.isFlagLoading ? (
          <div data-testid="tool-approval-flag-loading">
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <DataTable<BrowserAiToolListItem>
            id={tableId}
            mode="server"
            initialState={initialState}
            className="flex flex-col"
            containerClassName={SHADOW_AI_TABLE_SCROLL_CLASS}
            columns={columns}
            errorBoundaryLabel="Something went wrong in the Browser AI Tools table"
            search={{
              placeholder: 'Search browser AI tools...',
              className: 'w-[260px]',
            }}
            features={{
              enableSorting: true,
              // Scoped: a row opens a detail dialog, so there is nothing to expand.
              enableRowExpansion: !scopeUserId,
              // See DataTableRunningNowTab for why URL state is off when scoped.
              enableUrlState: !scopeUserId,
            }}
            onRowClick={scopeUserId ? openDetail : undefined}
            rowExpansion={{
              getRowId: getBrowserToolRowId,
              render: row => (
                <ExpandedToolRow
                  organizationId={organizationId}
                  row={row}
                  scopeUserId={scopeUserId}
                  onUserClick={
                    onUserClick &&
                    ((userId, userInfo) =>
                      onUserClick(userId, row.domain, userInfo))
                  }
                />
              ),
              contentClassName: 'bg-muted/30 p-4',
              rowClassName: 'bg-muted/10',
              keepMounted: false,
              showContainer: false,
              highlightOnExpand: true,
              scrollToExpandedRow: true,
            }}
            queryFn={scopeUserId ? queryFnWithDeepLink : queryFn}
            getQueryKeysFromParams={queryKeyFn}
            noDataLabel="No browser AI tools found."
            queryConfig={queryConfig}
            countScope={countScope}
            {...approvalTable.dataTableProps}
          />
        )}
      </CardContent>
      <BrowserHistoryDetailDialog
        tool={detailTool}
        onClose={() => setDetailTool(null)}
      />
    </Card>
  );
}

DataTableBrowserAiToolsContent.displayName = 'DataTableBrowserAiToolsContent';
